import { useCallback } from "react";

/**
 * Hook to get and set hash query parameters in the URL
 */
export const useHashParams = () => {
  const splitHash = useCallback(() => {
    const raw = window.location.hash.replace(/^#/, ""); // "/path?x=1"
    const [path, queryString] = raw.split("?");
    const params = new URLSearchParams(queryString || "");
    return { path: path || "/", params };
  }, []);

  const writeHash = useCallback((path: string, params: URLSearchParams) => {
    const query = params.toString();
    const hash = query ? `${path}?${query}` : path;

    // Hash-routing puro: pathname siempre "/"
    window.history.replaceState(null, "", `/#${hash}`);
  }, []);

  const getParam = useCallback(
    (key: string): string | null => {
      const { params } = splitHash();
      return params.get(key);
    },
    [splitHash]
  );

  const setParam = useCallback(
    (key: string, value: string) => {
      const { path, params } = splitHash();
      params.set(key, value);
      writeHash(path, params);
    },
    [splitHash, writeHash]
  );

  const deleteParam = useCallback(
    (key: string) => {
      const { path, params } = splitHash();
      params.delete(key);
      writeHash(path, params);
    },
    [splitHash, writeHash]
  );

  const replaceParams = useCallback(
    (newParams: Record<string, string>) => {
      const { path } = splitHash();
      const params = new URLSearchParams();
      Object.entries(newParams).forEach(([k, v]) => params.set(k, v));
      writeHash(path, params);
    },
    [splitHash, writeHash]
  );

  const getAllParams = useCallback((): Record<string, string> => {
    const { params } = splitHash();
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }, [splitHash]);

  const getFullHash = useCallback(
    (newParams: Record<string, string>) => {
      const { path, params } = splitHash();
      Object.entries(newParams).forEach(([k, v]) => params.set(k, v));
      const query = params.toString();
      return query ? `${path}?${query}` : path;
    },
    [splitHash]
  );

  return {
    getParam,
    setParam,
    deleteParam,
    replaceParams,
    getAllParams,
    getFullHash,
  };
};
